import { useEffect, useRef, useState } from "react";
import { useMenuDrawerContext } from "../../../../components/menu/MenuDrawerContextProvider.shared";
import { useCreateTwoBusinessRegistrationApplication } from "./hooks/useCreateTwoBusinessRegistrationApplication";
import { useWarnOnUnsavedChanges } from "./hooks/useWarnOnUnsavedChanges";
import {
  ProprietorDetailsForm,
  ProprietorDetailsFormType,
} from "./components/ProprietorDetailsForm";
import {
  BusinessDetailsForm,
  BusinessDetailsFormType,
} from "./components/BusinessDetailsForm";
import { BusinessRegistrationSubmitted } from "./components/BusinessRegistrationSubmitted";
import { useRemoteFeatureFlags } from "../../../../hooks/useRemoteFeatureFlags";
import { useNavigate } from "react-router";
import { DISCOVER } from "../../../../constants/routes";
import { useGetTwoBusinessRegistrationApplication } from "./hooks/useGetTwoBusinessRegistrationApplication";
import { apiV2AssertError } from "../../../../hooks/api/v2/apiV2Request";

export const TwoBusinessRegistrationApplication = () => {
  const { isActive } = useRemoteFeatureFlags();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isSellarCreditApplicationForSoleTradersEnabled = isActive(
    "enableSellarCreditForSoleTraders",
  );

  const navigate = useNavigate();

  const { setDisplayTypeOverride } = useMenuDrawerContext();
  const [businessDetails, setBusinessDetails] =
    useState<BusinessDetailsFormType>();
  const [proprietorDetails, setProprietorDetails] =
    useState<ProprietorDetailsFormType>();

  const [page, setPage] = useState<
    | "personalDetails"
    | "businessDetails"
    | "businessRegistrationSubmitted"
    | undefined
  >(undefined);

  const scrollRef = useRef<HTMLDivElement>(null);

  const businessRegistrationApplicationResponse =
    useGetTwoBusinessRegistrationApplication();

  const registrationPage = businessRegistrationApplicationResponse.isFetched
    ? businessRegistrationApplicationResponse.data?.id
      ? "businessRegistrationSubmitted"
      : "personalDetails"
    : undefined;
  const [previousRegistrationPage, setPreviousRegistrationPage] =
    useState(registrationPage);
  if (registrationPage !== previousRegistrationPage) {
    setPreviousRegistrationPage(registrationPage);
    if (registrationPage) {
      // if registration already exists, show submission page
      setPage(registrationPage);
    }
  }

  // on page change, scroll to top of page

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [page]);

  // if ff not enabled, redirect

  useEffect(() => {
    if (!isSellarCreditApplicationForSoleTradersEnabled) {
      window.location.replace(`${window.location.origin}`);
    }
  }, [isSellarCreditApplicationForSoleTradersEnabled]);

  useWarnOnUnsavedChanges(true);

  useEffect(() => {
    setDisplayTypeOverride("hidden");

    return () => {
      setDisplayTypeOverride(undefined);
    };
  }, [setDisplayTypeOverride]);

  const createTwoBusinessRegistrationApplication =
    useCreateTwoBusinessRegistrationApplication();

  return (
    <div className="h-full overflow-auto" ref={scrollRef}>
      <div className="mx-auto max-w-6xl overflow-auto p-6">
        <img
          src="https://sellar.imgix.net/sellar-image-assets/sellar-credit/sellar-credit-account-set-up.png"
          className="mx-auto mb-9 w-[236px]"
          width={236}
          height={24}
        />

        {errorMessage && (
          <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 text-red-600">
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>

              <div>
                <p className="font-medium text-red-800">Something went wrong</p>
                <p className="mt-1 text-sm text-red-700">{errorMessage}</p>
              </div>
            </div>
          </div>
        )}

        {page === "personalDetails" && (
          <>
            <h1 className="mb-8 text-2xl font-semibold">
              Business Registration Application
            </h1>
            <section className="mb-12 rounded-lg border border-gray-200 bg-gray-50 p-6">
              <p className="mb-4 text-base leading-relaxed text-gray-800">
                This form collects the necessary information to register your
                business with <strong>Two Inc</strong>. It is specifically for
                sole traders and businesses not listed in national registries.
                Completing this registration allows Two Inc to verify your
                business and determine your eligibility for credit. The process
                has 6 sections and should take approximately{" "}
                <span className="font-semibold text-gray-900">
                  5–10 minutes
                </span>{" "}
                to complete.
              </p>
              <ul className="list-disc space-y-2 pl-6 text-gray-600">
                <li>
                  Following submission, you will receive an email from Two Inc.
                  requesting that you submit evidence related to your
                  application. As part of this process, Two will also need to
                  review your bank account.
                  <br />{" "}
                  <strong>
                    Please ensure you provide details for your primary business
                    or personal bank account, with a transaction history of at
                    least the last 6 months.
                  </strong>{" "}
                  Newly opened or temporary accounts cannot be accepted for
                  verification.
                </li>
                <li>
                  Once registration is complete, a credit check will be
                  automatically carried out and you will be notified of the
                  outcome of your credit application.
                </li>
                <li>
                  Providing accurate information ensures Two Inc can quickly
                  assess the eligibility of your company.
                </li>
              </ul>
            </section>
            <ProprietorDetailsForm
              defaultValues={proprietorDetails}
              onSave={(data) => {
                setProprietorDetails(data);
                setPage("businessDetails");
              }}
            />
          </>
        )}
        {page === "businessDetails" && (
          <BusinessDetailsForm
            defaultValues={businessDetails}
            isLoading={createTwoBusinessRegistrationApplication.isPending}
            onSubmit={(data) => {
              setBusinessDetails(data);

              if (proprietorDetails && businessDetails) {
                createTwoBusinessRegistrationApplication
                  .mutateAsync({
                    data: {
                      proposedBusiness: {
                        ...data,
                        proprietorDetails,
                      },
                    },
                  })
                  .then(() => {
                    setErrorMessage(null);
                    setPage("businessRegistrationSubmitted");
                  })
                  .catch((error) => {
                    console.error(error);

                    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });

                    const errorDetails =
                      apiV2AssertError(error)?.response?.data.message ??
                      "Unknown error";

                    setErrorMessage(
                      `We couldn’t submit your application. Please check your details and try again. Details: ${errorDetails}`,
                    );
                  });
              }
            }}
            onBack={(data) => {
              setBusinessDetails(data);
              setPage("personalDetails");
            }}
          />
        )}
        {page === "businessRegistrationSubmitted" && (
          <BusinessRegistrationSubmitted
            onDone={() => {
              navigate(DISCOVER);
            }}
          />
        )}
      </div>
    </div>
  );
};
